"use client";

import { Badge, Button, Loader } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ShopProductClaimListQueryDTO } from "@/modules/merchant/domain/contracts";
import {
  canUseMerchantPermission,
  type SupplierListRow,
  shopSuppliersApi,
} from "@/modules/merchant/presentation/api/client";
import {
  useShopBranchesListQuery,
  useShopProductClaimsListQuery,
  useShopWarrantiesExpiringSoonQuery,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  claimIssueTypeLabel,
  claimStatusBadge,
  warrantyEligibilityLabel,
} from "./shop-product-claim-helpers";

const FILTER_ALL = "__all__";

type ClaimsSearch = {
  offset: number;
  limit: number;
  branchId?: string;
  status?: string;
  supplierId?: string;
  warrantyLink?: "linked" | "none";
};

export function ShopProductClaimsPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate();
  const search = useSearch({
    from: "/app/shops/$shopId/product-claims",
  }) as ClaimsSearch;

  const { currentShop, currentPermissions } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const defaultBranchId = currentShop?.branchId ?? "";

  const branchesQuery = useShopBranchesListQuery(shopId);
  const branches = branchesQuery.data ?? [];

  const limit = Math.min(100, Math.max(1, search.limit ?? 20));
  const offset = Math.max(0, search.offset ?? 0);

  const branchFromUrl = search.branchId?.trim() ?? "";
  const statusKey = search.status?.trim() ?? "";
  const statusFilter =
    statusKey === "draft" ||
    statusKey === "submitted" ||
    statusKey === "under_review" ||
    statusKey === "resolved" ||
    statusKey === "rejected" ||
    statusKey === "canceled"
      ? statusKey
      : FILTER_ALL;

  const warrantyLinkFilter =
    search.warrantyLink === "linked" || search.warrantyLink === "none"
      ? search.warrantyLink
      : FILTER_ALL;

  const effectiveBranchId =
    branchFromUrl ||
    defaultBranchId ||
    (branches.length === 1 ? branches[0]?.id : "") ||
    "";

  useEffect(() => {
    if (!shopId || branchFromUrl || !defaultBranchId) return;
    void nav({
      to: "/app/shops/$shopId/product-claims",
      params: { shopId },
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
        branchId: defaultBranchId,
        status: search.status,
        supplierId: search.supplierId,
        warrantyLink: search.warrantyLink,
      },
      replace: true,
    });
  }, [shopId, branchFromUrl, defaultBranchId, nav, search]);

  const statusForApi: ShopProductClaimListQueryDTO["status"] | undefined =
    statusFilter === FILTER_ALL
      ? undefined
      : (statusFilter as ShopProductClaimListQueryDTO["status"]);

  const supplierFilterId = search.supplierId?.trim() ?? "";
  const supplierIdForList =
    supplierFilterId.length > 0 ? supplierFilterId : undefined;

  const warrantyLinkForApi: ShopProductClaimListQueryDTO["warrantyLink"] =
    warrantyLinkFilter === FILTER_ALL ? "any" : warrantyLinkFilter;

  const listQuery = useShopProductClaimsListQuery(
    shopId,
    effectiveBranchId
      ? {
          branchId: effectiveBranchId,
          status: statusForApi,
          supplierId: supplierIdForList,
          warrantyLink: warrantyLinkForApi,
          limit,
          offset,
        }
      : undefined,
  );

  const items = listQuery.data?.items ?? [];
  const meta = listQuery.data?.meta;
  const total = meta?.total ?? 0;
  const pageEnd = Math.min(offset + limit, total);
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;

  const canCreate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.claims.create,
    verifiedLevel,
  );
  const canReadPos = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.read,
    verifiedLevel,
  );

  const expiringWarrantiesQuery = useShopWarrantiesExpiringSoonQuery(
    shopId,
    effectiveBranchId && canReadPos
      ? {
          branchId: effectiveBranchId,
          withinDays: 30,
          limit: 8,
          offset: 0,
        }
      : undefined,
  );

  const branchOptions = useMemo(
    () =>
      branches.map((b) => ({
        value: b.id,
        label: b.name,
      })),
    [branches],
  );

  return (
    <RequireMerchantPermission permission={MerchantPermissions.claims.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.productClaims(shopId)} />
      </Header>

      <Main>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading font-semibold text-xl tracking-tight">
              ເຄສສິນຄ້າມີບັນຫາ
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ບັນທຶກ ຕິດຕາມສະຖານະ ຜູກບິນຂາຍ/lot/serial ແລະ ແນບຫຼັຖຖານ (Claim C1)
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SimpleSelect
              value={effectiveBranchId || ""}
              onValueChange={(v) => {
                if (!shopId) return;
                void nav({
                  to: "/app/shops/$shopId/product-claims",
                  params: { shopId },
                  search: {
                    offset: 0,
                    limit,
                    branchId: v,
                    status: search.status,
                    supplierId: search.supplierId,
                    warrantyLink: search.warrantyLink,
                  },
                });
              }}
              className="h-9 min-w-[200px]"
              placeholder="ສາຂາ"
              disabled={branchOptions.length === 0}
              options={branchOptions}
            />
            <SimpleSelect
              value={statusFilter}
              onValueChange={(v) => {
                if (!shopId) return;
                void nav({
                  to: "/app/shops/$shopId/product-claims",
                  params: { shopId },
                  search: {
                    offset: 0,
                    limit,
                    branchId: effectiveBranchId || undefined,
                    status: v === FILTER_ALL ? undefined : v,
                    supplierId: search.supplierId,
                    warrantyLink: search.warrantyLink,
                  },
                });
              }}
              className="h-9 w-44"
              placeholder="ສະຖານະ"
              options={[
                { value: FILTER_ALL, label: "ທັງໝົດ" },
                { value: "draft", label: "ຮ່າງ" },
                { value: "submitted", label: "ສົ່ງແລ້ວ" },
                { value: "under_review", label: "ກຳລັງກວດ" },
                { value: "resolved", label: "ປິດເຄສ" },
                { value: "rejected", label: "ປະຕິເສດ" },
                { value: "canceled", label: "ຍົກເລີກ" },
              ]}
            />
            <SimpleSelect
              value={warrantyLinkFilter}
              onValueChange={(v) => {
                if (!shopId) return;
                void nav({
                  to: "/app/shops/$shopId/product-claims",
                  params: { shopId },
                  search: {
                    offset: 0,
                    limit,
                    branchId: effectiveBranchId || undefined,
                    status: search.status,
                    supplierId: search.supplierId,
                    warrantyLink:
                      v === FILTER_ALL ? undefined : (v as "linked" | "none"),
                  },
                });
              }}
              className="h-9 w-44"
              placeholder="ປະກັນ"
              options={[
                { value: FILTER_ALL, label: "ປະກັນ: ທັງໝົດ" },
                { value: "linked", label: "ຜູກປະກັນ" },
                { value: "none", label: "ບໍ່ຜູກ" },
              ]}
            />
            <InfiniteCombobox<SupplierListRow>
              queryKey={[
                "shops",
                shopId ?? "",
                "product-claims",
                "supplier-filter",
              ]}
              queryFn={async ({ search: q, pageParam }) => {
                if (!shopId) {
                  return { items: [] as SupplierListRow[], nextPage: null };
                }
                const res = await shopSuppliersApi.list(shopId, {
                  limit: 20,
                  offset: (pageParam - 1) * 20,
                  search: q.trim() || undefined,
                  activeOnly: true,
                });
                const hasMore =
                  res.meta.offset + res.data.length < res.meta.total;
                return {
                  items: res.data,
                  nextPage: hasMore ? pageParam + 1 : null,
                };
              }}
              preloadQueryFn={async (id) => {
                if (!shopId || !id) return null;
                const res = await shopSuppliersApi.list(shopId, {
                  limit: 200,
                  offset: 0,
                  activeOnly: false,
                });
                return res.data.find((r) => r.id === id) ?? null;
              }}
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              value={supplierFilterId}
              onValueChange={(val) => {
                if (!shopId) return;
                void nav({
                  to: "/app/shops/$shopId/product-claims",
                  params: { shopId },
                  search: {
                    offset: 0,
                    limit,
                    branchId: effectiveBranchId || undefined,
                    status: search.status,
                    supplierId: val.trim() !== "" ? val : undefined,
                    warrantyLink: search.warrantyLink,
                  },
                });
              }}
              placeholder="ຜູ້ສະໜອງ"
              className="h-9 w-full min-w-[180px] sm:w-56"
              clearable
            />
            {canCreate ? (
              <Button
                type="button"
                className="h-9 gap-1"
                disabled={!effectiveBranchId || !shopId}
                onClick={() => {
                  if (!shopId || !effectiveBranchId) return;
                  void nav({
                    to: "/app/shops/$shopId/product-claims/new",
                    params: { shopId },
                    search: {
                      branchId: effectiveBranchId,
                      supplierId: search.supplierId,
                    },
                  });
                }}
              >
                <PlusIcon className="size-4" />
                ສ້າງເຄສ
              </Button>
            ) : null}
          </div>
        </div>

        {canReadPos && effectiveBranchId ? (
          <div className="mb-4 rounded-xl border border-sky-500/30 bg-muted/25 p-4">
            <h2 className="font-heading font-medium text-foreground text-sm">
              ປະກັນຕາມບິນໃກ້ຫມົດອາຍຸ (30 ວັນ)
            </h2>
            <p className="mt-0.5 text-muted-foreground text-xs">
              ສາຂາປັດຈຸບັນ — ສິນຄ້າທີ່ຍັງ active ແລະ ວັນຫມົດຄຸ້ມຄອງຢູ່ໃນຊ່ວງນີ້
            </p>
            {expiringWarrantiesQuery.isLoading ? (
              <div className="flex justify-center py-6">
                <Loader />
              </div>
            ) : expiringWarrantiesQuery.isError ? (
              <p className="mt-2 text-muted-foreground text-xs">
                ໂຫຼດລາຍງານບໍ່ສຳເລັດ
              </p>
            ) : (expiringWarrantiesQuery.data?.items.length ?? 0) === 0 ? (
              <p className="mt-2 text-muted-foreground text-xs">
                ບໍ່ມີລາຍການໃນຊ່ວງນີ້
              </p>
            ) : (
              <>
                <ul className="mt-3 divide-y text-sm">
                  {(expiringWarrantiesQuery.data?.items ?? []).map((row) => (
                    <li key={row.id} className="flex flex-wrap gap-x-4 gap-y-1 py-2 first:pt-0">
                      <span className="min-w-0 font-mono text-foreground text-xs">
                        {row.shopSku}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        ບິນ {row.saleNumber}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        ຫມົດ: {formatDateLocal(row.coverageEndAt)}
                      </span>
                      {row.coverageEndOdometerKm != null ? (
                        <span className="text-muted-foreground text-xs">
                          ເກນ km: {row.coverageEndOdometerKm.toLocaleString()}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {expiringWarrantiesQuery.data &&
                expiringWarrantiesQuery.data.meta.total >
                  expiringWarrantiesQuery.data.items.length ? (
                  <p className="mt-2 text-muted-foreground text-xs">
                    ສະແດງ {expiringWarrantiesQuery.data.items.length} ຈາກ{" "}
                    {expiringWarrantiesQuery.data.meta.total} ລາຍການ — ຫຼຸດຊ່ວງ
                    ຫຼືເພີ່ມ limit ໃນອະນາຄົດ
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {!effectiveBranchId ? (
          <p className="text-muted-foreground text-sm">
            ເລືອກສາຂາໃນບັນຊີ ຫຼື ຕັ້ງຄ່າພະນັກງານໃຫ້ມີສາຂາເພື່ອເບິ່ງລາຍການ.
          </p>
        ) : null}

        <div className="rounded-xl border bg-card">
          {listQuery.isLoading ? (
            <p className="p-6 text-muted-foreground text-sm">ກໍາລັງໂຫຼດ...</p>
          ) : items.length === 0 && total === 0 ? (
            <p className="p-6 text-muted-foreground text-sm">
              ຍັງບໍ່ມີເຄສ. ກົດ &quot;ສ້າງເຄສ&quot; ເພື່ອເລີ່ມຕົ້ນ.
            </p>
          ) : items.length === 0 ? (
            <p className="p-6 text-muted-foreground text-sm">ບໍ່ມີລາຍການໃນໜ້ານີ້.</p>
          ) : (
            <ul className="divide-y">
              {items.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="w-full p-4 text-left transition hover:bg-muted/40"
                    onClick={() => {
                      if (!shopId) return;
                      void nav({
                        to: "/app/shops/$shopId/product-claims/$claimId",
                        params: { shopId, claimId: row.id },
                      });
                    }}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">
                          {row.title?.trim()
                            ? row.title
                            : claimIssueTypeLabel(row.issueType)}
                        </p>
                        <p className="mt-1 text-muted-foreground text-sm">
                          {row.branchName} ·{" "}
                          {claimIssueTypeLabel(row.issueType)}
                          {row.supplierName ? (
                            <span className="ml-1">· {row.supplierName}</span>
                          ) : null}
                        </p>
                        {row.saleLineWarrantyId ||
                        row.warrantyEligibilityAtSubmit ? (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {row.saleLineWarrantyId ? (
                              <Badge
                                variant="outline"
                                className="border-sky-500/40 text-sky-800 text-xs dark:text-sky-200"
                              >
                                ຜູກສິດປະກັນ
                              </Badge>
                            ) : null}
                            {row.warrantyEligibilityAtSubmit ? (
                              <Badge variant="secondary" className="text-xs">
                                {warrantyEligibilityLabel(
                                  row.warrantyEligibilityAtSubmit,
                                )}
                              </Badge>
                            ) : null}
                          </div>
                        ) : null}
                        <p className="mt-2 text-muted-foreground text-xs">
                          ສ້າງ: {formatDateLocal(row.createdAt)}
                          {row.submittedAt ? (
                            <span className="ml-2">
                              · ສົ່ງ: {formatDateLocal(row.submittedAt)}
                            </span>
                          ) : null}
                        </p>
                      </div>
                      <div className="shrink-0">
                        {claimStatusBadge(row.status)}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!listQuery.isLoading && total > 0 ? (
            <div className="flex flex-col gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-muted-foreground text-sm">
                {`${offset + 1}–${pageEnd} ຈາກ ${total}`}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!hasPrev}
                  onClick={() =>
                    shopId &&
                    void nav({
                      to: "/app/shops/$shopId/product-claims",
                      params: { shopId },
                      search: {
                        offset: Math.max(0, offset - limit),
                        limit,
                        branchId: effectiveBranchId || undefined,
                        status: search.status,
                        supplierId: search.supplierId,
                        warrantyLink: search.warrantyLink,
                      },
                    })
                  }
                >
                  ກ່ອນໜ້າ
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!hasNext}
                  onClick={() =>
                    shopId &&
                    void nav({
                      to: "/app/shops/$shopId/product-claims",
                      params: { shopId },
                      search: {
                        offset: offset + limit,
                        limit,
                        branchId: effectiveBranchId || undefined,
                        status: search.status,
                        supplierId: search.supplierId,
                        warrantyLink: search.warrantyLink,
                      },
                    })
                  }
                >
                  ຖັດໄປ
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
